import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {useTranslation} from 'react-i18next'
import type {IconGradientStop} from '../../../common/utils/iconRenderer'
import {trackEvent} from '../../../common/analytics/trackEvent'

interface IconBackgroundPickerProps {
  bgMode: 'color' | 'gradient'
  bgColor: string
  bgGradient: IconGradientStop[]
  bgAngle: number
  onModeChange: (mode: 'color' | 'gradient') => void
  onColorChange: (color: string) => void
  onGradientChange: (stops: IconGradientStop[]) => void
  onAngleChange: (angle: number) => void
}

const MARKER_HIT_RADIUS = 0.04

const buildGradientCSS = (stops: IconGradientStop[]): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  const parts = sorted.map((s) => `${s.color} ${s.position * 100}%`)
  return `linear-gradient(90deg, ${parts.join(', ')})`
}

const interpolateColorAt = (stops: IconGradientStop[], pos: number): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  if (sorted.length === 0) return '#ffffff'
  if (pos <= sorted[0].position) return sorted[0].color
  if (pos >= sorted[sorted.length - 1].position) return sorted[sorted.length - 1].color
  for (let i = 0; i < sorted.length - 1; i++) {
    const left = sorted[i]
    const right = sorted[i + 1]
    if (pos >= left.position && pos <= right.position) {
      const t = (pos - left.position) / (right.position - left.position)
      const lR = parseInt(left.color.slice(1, 3), 16)
      const lG = parseInt(left.color.slice(3, 5), 16)
      const lB = parseInt(left.color.slice(5, 7), 16)
      const rR = parseInt(right.color.slice(1, 3), 16)
      const rG = parseInt(right.color.slice(3, 5), 16)
      const rB = parseInt(right.color.slice(5, 7), 16)
      const r = Math.round(lR + (rR - lR) * t)
      const g = Math.round(lG + (rG - lG) * t)
      const b = Math.round(lB + (rB - lB) * t)
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    }
  }
  return sorted[0].color
}

const generateStopId = (): string => `s_${Math.random().toString(36).slice(2, 9)}`

const IconBackgroundPicker = ({
  bgMode,
  bgColor,
  bgGradient,
  bgAngle,
  onModeChange,
  onColorChange,
  onGradientChange,
  onAngleChange,
}: IconBackgroundPickerProps) => {
  const {t} = useTranslation()
  const barRef = useRef<HTMLDivElement>(null)
  const draggingIdRef = useRef<string | null>(null)
  const [selectedStopId, setSelectedStopId] = useState<string>(bgGradient[0]?.id ?? '')

  const selectedStop = bgGradient.find((s) => s.id === selectedStopId) ?? bgGradient[0] ?? null

  const positionFromX = useCallback((clientX: number): number => {
    if (!barRef.current) return 0
    const rect = barRef.current.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  }, [])

  const handleBarClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).dataset.marker) return
      const pos = positionFromX(e.clientX)
      const tooClose = bgGradient.some((s) => Math.abs(s.position - pos) < MARKER_HIT_RADIUS)
      if (tooClose) return
      const newStop: IconGradientStop = {
        id: generateStopId(),
        color: interpolateColorAt(bgGradient, pos),
        position: Math.round(pos * 1000) / 1000,
      }
      onGradientChange([...bgGradient, newStop])
      setSelectedStopId(newStop.id)
    },
    [bgGradient, onGradientChange, positionFromX],
  )

  const handleMarkerPointerDown = useCallback(
    (stopId: string, e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedStopId(stopId)
      draggingIdRef.current = stopId

      const handleMove = (ev: PointerEvent) => {
        const id = draggingIdRef.current
        if (!id) return
        const pos = positionFromX(ev.clientX)
        const next = bgGradient.map((s) => (s.id === id ? {...s, position: Math.round(pos * 1000) / 1000} : s))
        onGradientChange(next)
      }

      const handleUp = () => {
        draggingIdRef.current = null
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        window.removeEventListener('pointercancel', handleUp)
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
      window.addEventListener('pointercancel', handleUp)
    },
    [bgGradient, onGradientChange, positionFromX],
  )

  const handleStopColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!selectedStop) return
      const next = bgGradient.map((s) => (s.id === selectedStop.id ? {...s, color: e.target.value} : s))
      onGradientChange(next)
    },
    [bgGradient, onGradientChange, selectedStop],
  )

  const handleDeleteStop = useCallback(() => {
    if (!selectedStop || bgGradient.length <= 2) return
    const next = bgGradient.filter((s) => s.id !== selectedStop.id)
    onGradientChange(next)
    setSelectedStopId(next[0]?.id ?? '')
  }, [bgGradient, onGradientChange, selectedStop])

  const handleAngleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onAngleChange(parseInt(e.target.value, 10))
    },
    [onAngleChange],
  )

  return (
    <div className="icg-bg">
      <div className="icg-tabs">
        <button
          type="button"
          className={`icg-tab${bgMode === 'color' ? ' is-active' : ''}`}
          onClick={() => {
            onModeChange('color')
            trackEvent('icon_bg_mode_changed', {mode: 'solid'})
          }}
        >
          {t('iconGenerator.bg.color')}
        </button>
        <button
          type="button"
          className={`icg-tab${bgMode === 'gradient' ? ' is-active' : ''}`}
          onClick={() => {
            onModeChange('gradient')
            trackEvent('icon_bg_mode_changed', {mode: 'gradient'})
          }}
        >
          {t('iconGenerator.bg.gradient')}
        </button>
      </div>

      {bgMode === 'color' && (
        <div className="icg-row">
          <label className="icg-label">{t('iconGenerator.bg.fillColor')}</label>
          <input
            type="color"
            className="icg-color-input"
            value={bgColor}
            onChange={(e) => onColorChange(e.target.value)}
          />
        </div>
      )}

      {bgMode === 'gradient' && (
        <div className="icg-gradient">
          <div
            ref={barRef}
            className="icg-gradient-bar"
            style={{background: buildGradientCSS(bgGradient)}}
            onClick={handleBarClick}
          >
            {bgGradient.map((stop) => {
              const isSel = selectedStop?.id === stop.id
              return (
                <div
                  key={stop.id}
                  data-marker="true"
                  className={`icg-gradient-marker${isSel ? ' is-selected' : ''}`}
                  style={{left: `${stop.position * 100}%`, background: stop.color}}
                  onPointerDown={(e) => handleMarkerPointerDown(stop.id, e)}
                />
              )
            })}
          </div>

          {selectedStop && (
            <div className="icg-row">
              <label className="icg-label">{t('iconGenerator.bg.stopColor')}</label>
              <input
                type="color"
                className="icg-color-input"
                value={selectedStop.color}
                onChange={handleStopColorChange}
              />
              {bgGradient.length > 2 && (
                <button type="button" className="icg-btn icg-btn-ghost" onClick={handleDeleteStop}>
                  {t('iconGenerator.bg.removeStop')}
                </button>
              )}
            </div>
          )}

          <label className="icg-slider">
            <span className="icg-slider-label">{t('iconGenerator.bg.angle')}</span>
            <input type="range" min={0} max={360} step={1} value={bgAngle} onChange={handleAngleChange} />
            <span className="icg-slider-value">{bgAngle}°</span>
          </label>
          <p className="icg-hint">{t('iconGenerator.bg.tip')}</p>
        </div>
      )}
    </div>
  )
}

export default IconBackgroundPicker
